import type { CartItemDto } from '@bondsports/types';
import { cn } from '@utils';
import * as React from 'react';
import { CartItem } from '../cart-item/cart-item';
import type { CartItemProps } from '../cart-item/types';
import { isPriceValid } from '../util';
import { CartAvatarBox } from './components/cart-avatar/cart-avatar';

interface CartTreeProps extends Omit<CartItemProps, 'item'> {
	items: CartItemDto[];
}

interface CartItemTreeProps extends CartItemProps {
	isChild?: boolean;
	height?: number;
}

// styling
// the height of the curve box in rem. this is spacing 7.5
const CURVE_BOX_SIZE = 2.4;

const PADDING_Y = 2.4;

const CURVE_BOX_SIZE_AND_PADDING_Y = `${CURVE_BOX_SIZE + PADDING_Y}rem`;

/**
 * Calculates the height of the tree member based on the number of tree members
 * this is the height of the line between the parent and the last child tree member
 * we need to subtract the curve box height so we wont see an open end
 * @param item - the number of tree members
 * @param height - the height of the child
 * @returns the height of the tree member
 */
const getTreeMemberHeight = (item: CartItemDto, height?: number) => {
	const numberOfDirectChildren = item.children?.length ?? 0;

	if (numberOfDirectChildren === 1) {
		return `calc(100% - ${CURVE_BOX_SIZE_AND_PADDING_Y})`;
	}

	return `calc(${height}px + 100% - ${CURVE_BOX_SIZE_AND_PADDING_Y})`;
};

// styling

export const CartTree = ({ items, ...props }: CartTreeProps) => {
	return (
		<div className="bg-highlightBorder flex flex-col gap-px">
			{items.map((item) => {
				return (
					<div key={item.id} className={cn('bg-white')}>
						<CartItemTree item={item} {...props} />
					</div>
				);
			})}
		</div>
	);
};

const CartItemTree = React.forwardRef<HTMLDivElement, CartItemTreeProps>(({ item, isChild = false, ...props }, ref) => {
	const [node, setNode] = React.useState<HTMLDivElement | null>(null);

	const hasChildren = !!item.children?.length;
	const childs = item.children?.slice(0, -1) ?? [];
	const lastChild = item.children?.[item.children.length - 1];

	const height = node?.getBoundingClientRect().height;
	const isGroup = !isChild && !isPriceValid(item.price);

	return (
		<div ref={ref} className={cn('relative flex w-full flex-col items-center', isChild && 'pl-[4.8rem]')}>
			<div className="relative flex w-full flex-row gap-2 py-2">
				{hasChildren && <TreeMemberLine height={height} item={item} />}
				{!!isChild && <CurveBox />}
				<CartAvatarBox item={item} />
				<CartItem key={item.id} item={item} isChild={isChild} {...props} />
			</div>
			{hasChildren && (
				<>
					<div ref={setNode} className="w-full">
						{childs?.map(child => {
							return (
								<CartItemTree
									key={child.id}
									item={child}
									isChild
									{...props}
									disableDelete={props.disableDelete || isGroup}
								/>
							);
						})}
					</div>
					{lastChild && (
						<div className="w-full">
							<CartItemTree
								key={lastChild.id}
								item={lastChild}
								isChild
								{...props}
								disableDelete={props.disableDelete || isGroup}
							/>
						</div>
					)}
				</>
			)}
		</div>
	);
});

CartItemTree.displayName = 'CartItemTree';

/**
 * The curve box is the box that is used to create the curve effect
 * the height is the curve box height ${CURVE_BOX_HEIGHT}rem
 * the width is the gap between item and avatar + half of the avatar width - 0.4rem for gap between avatar and curve box
 * top is CURVE_BOX_HEIGHT - 2/1 of the avatar box height
 * left is the width without the gap added to the width
 * @returns the curve box
 */
const CurveBox = () => (
	<div
		aria-hidden
		className="border-highlightBorder absolute left-[-2.8rem] top-[0.8rem] z-10 h-[2.4rem] w-[2.4rem] rounded-bl-3xl border-b border-l"
	/>
);

/**
 * The tree member line is the line that is used to connect the parent and the child tree members
 * @param numberOfTreeMembers - the number of tree members to calculate the height of the line
 * @param height - the height of the child
 * @returns the tree member line
 *
 * the top is the avatar size + cart item padding + 0.4rem of padding
 * the left is half of the avatar width
 * the height is the height of the cart items (100%) - top -
 * the width of the line is 1px
 */
const TreeMemberLine = ({ item, height }: { item: CartItemDto; height?: number }) => {
	return (
		<div
			aria-hidden
			style={{
				height: getTreeMemberHeight(item, height),
			}}
			className={cn('border-highlightBorder absolute left-4 top-[5.6rem] z-20 border-l')}
		/>
	);
};
